/**
 * ExecutionProgressBar Component
 *
 * Displays detailed execution progress for batch job execution with:
 * - Horizontal stacked progress bar with colored segments
 * - Percentage completion
 * - Detailed breakdown (Running, Completed, Failed, Pending)
 * - Smooth CSS transitions for real-time updates
 */

import React from 'react';

export interface ExecutionProgressBarProps {
  batchId: string;
  totalJobs: number;
  running: number;
  completed: number;
  failed: number;
  pending: number;
}

/**
 * ExecutionProgressBar Component
 *
 * Visual representation of batch execution progress with color-coded segments
 *
 * @example
 * <ExecutionProgressBar
 *   batchId="batch-123"
 *   totalJobs={100}
 *   running={3}
 *   completed={45}
 *   failed={2}
 *   pending={50}
 * />
 */
export const ExecutionProgressBar: React.FC<ExecutionProgressBarProps> = ({
  batchId,
  totalJobs,
  running,
  completed,
  failed,
  pending,
}) => {
  // Calculate percentages for each segment
  const completedPercent = totalJobs > 0 ? (completed / totalJobs) * 100 : 0;
  const failedPercent = totalJobs > 0 ? (failed / totalJobs) * 100 : 0;
  const runningPercent = totalJobs > 0 ? (running / totalJobs) * 100 : 0;
  const pendingPercent = totalJobs > 0 ? (pending / totalJobs) * 100 : 0;

  // Overall completion percentage (completed + failed out of total)
  const overallPercent = totalJobs > 0 ? ((completed + failed) / totalJobs) * 100 : 0;

  return (
    <div className="space-y-3" role="region" aria-label="Execution progress">
      {/* Progress Bar */}
      <div className="relative">
        {/* Container */}
        <div className="h-8 bg-gray-200 rounded-lg overflow-hidden shadow-inner">
          {/* Stacked segments */}
          <div className="h-full flex">
            {/* Completed segment (green) */}
            {completedPercent > 0 && (
              <div
                className="bg-green-500 transition-all duration-500 ease-in-out flex items-center justify-center"
                style={{ width: `${completedPercent}%` }}
                title={`${completed} completed`}
                aria-label={`${completed} jobs completed`}
              >
                {completedPercent > 10 && (
                  <span className="text-xs font-medium text-white">{completed}</span>
                )}
              </div>
            )}

            {/* Failed segment (red) */}
            {failedPercent > 0 && (
              <div
                className="bg-red-500 transition-all duration-500 ease-in-out flex items-center justify-center"
                style={{ width: `${failedPercent}%` }}
                title={`${failed} failed`}
                aria-label={`${failed} jobs failed`}
              >
                {failedPercent > 10 && (
                  <span className="text-xs font-medium text-white">{failed}</span>
                )}
              </div>
            )}

            {/* Running segment (yellow) */}
            {runningPercent > 0 && (
              <div
                className="bg-yellow-400 transition-all duration-500 ease-in-out flex items-center justify-center animate-pulse"
                style={{ width: `${runningPercent}%` }}
                title={`${running} running`}
                aria-label={`${running} jobs running`}
              >
                {runningPercent > 10 && (
                  <span className="text-xs font-medium text-gray-900">{running}</span>
                )}
              </div>
            )}

            {/* Pending segment (gray) */}
            {pendingPercent > 0 && (
              <div
                className="bg-gray-300 transition-all duration-500 ease-in-out flex items-center justify-center"
                style={{ width: `${pendingPercent}%` }}
                title={`${pending} pending`}
                aria-label={`${pending} jobs pending`}
              >
                {pendingPercent > 10 && (
                  <span className="text-xs font-medium text-gray-700">{pending}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Percentage label overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-sm font-bold text-gray-900 drop-shadow-md">
            {overallPercent.toFixed(0)}% Complete
          </span>
        </div>
      </div>

      {/* Detailed breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        {/* Running */}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse" aria-hidden="true"></div>
          <span className="text-gray-700">
            Running: <span className="font-semibold text-gray-900">{running}</span>
          </span>
        </div>

        {/* Completed */}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full" aria-hidden="true"></div>
          <span className="text-gray-700">
            Completed: <span className="font-semibold text-gray-900">{completed}</span>
          </span>
        </div>

        {/* Failed */}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full" aria-hidden="true"></div>
          <span className="text-gray-700">
            Failed: <span className="font-semibold text-gray-900">{failed}</span>
          </span>
        </div>

        {/* Pending */}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-gray-300 rounded-full" aria-hidden="true"></div>
          <span className="text-gray-700">
            Pending: <span className="font-semibold text-gray-900">{pending}</span>
          </span>
        </div>
      </div>

      {/* Total summary */}
      <div className="text-center text-sm text-gray-600">
        <span className="font-medium">{completed + failed}</span> of{' '}
        <span className="font-medium">{totalJobs}</span> jobs processed
      </div>
    </div>
  );
};
